import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import './Banner.css';

interface BannerShellProps {
  visible: boolean;
  isClosing: boolean;
  typeClass: 'info' | 'success' | 'warning' | 'critical';
  iconClass: string;
  message: string;
  onOk: () => void;
  extraActions?: React.ReactNode;
}

const CLOSE_ANIMATION_CLASS = {
  closing: 'fade-out',
  opening: 'slide-down',
};

const BannerShell: React.FC<BannerShellProps> = ({
  visible,
  isClosing,
  typeClass,
  iconClass,
  message,
  onOk,
  extraActions,
}) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const [animationDuration, setAnimationDuration] = useState(40);
  const messageRef = useRef<HTMLParagraphElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use useLayoutEffect to prevent flicker during DOM measurements
  useLayoutEffect(() => {
    if (!visible || !messageRef.current || !containerRef.current) return;

    const checkOverflow = () => {
      const messageElement = messageRef.current;
      const containerElement = containerRef.current;

      if (!messageElement || !containerElement) return;

      const messageWidth = messageElement.scrollWidth;
      const containerWidth = containerElement.clientWidth;

      // Only enable scrolling if text is significantly wider than container
      const needsScrolling = messageWidth > containerWidth * 1.1;

      // Calculate animation duration based on text length for smoother animation
      // Longer text should move slower for better readability
      if (needsScrolling) {
        // Calculate optimal duration: longer text = slower animation
        const calculatedDuration = Math.max(
          20,
          Math.min(60, messageWidth / 10),
        );
        setAnimationDuration(calculatedDuration);
      }

      setIsScrolling(needsScrolling);
    };

    // Initial check
    checkOverflow();

    // Check again after a short delay to ensure fonts are loaded
    const initialTimer = setTimeout(checkOverflow, 100);

    // Debounced resize handler
    let resizeTimer: NodeJS.Timeout | null = null;
    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkOverflow, 200);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer) clearTimeout(resizeTimer);
      clearTimeout(initialTimer);
    };
  }, [visible, message]);

  // Prevent animation from starting before component is fully rendered
  useEffect(() => {
    if (!isScrolling) return;

    // Small delay before starting animation to prevent initial jitter
    const timer = setTimeout(() => {
      if (messageRef.current) {
        messageRef.current.style.visibility = 'visible';
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [isScrolling]);

  if (!visible) return null;

  return (
    <div className="banner-container">
      <div
        className={`banner ${typeClass} ${
          isClosing
            ? CLOSE_ANIMATION_CLASS.closing
            : CLOSE_ANIMATION_CLASS.opening
        }`}
      >
        <span className="icon">
          <i className={iconClass} />
        </span>
        <div className="message-content">
          <div className="message-container" ref={containerRef}>
            <p
              ref={messageRef}
              className={`message ${isScrolling ? 'message-scrolling' : ''}`}
              style={{
                animationDuration: `${animationDuration}s`,
                visibility: isScrolling ? 'hidden' : 'visible', // Hide initially to prevent jitter
              }}
            >
              {isScrolling ? (
                <>
                  <span className="message-content-wrapper">{message}</span>
                  <span className="message-spacer" />
                  <span className="message-content-wrapper">{message}</span>
                  <span className="message-spacer" />
                </>
              ) : (
                message
              )}
            </p>
          </div>
        </div>
        <div className="banner-actions">
          {extraActions}
          <button className="banner-button ok-button" onClick={onOk}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default BannerShell;
